/**
 * Mapping des erreurs Supabase Auth vers des messages français user-friendly
 */

type ErrorMapping = {
  pattern: string
  message: string
}

const AUTH_ERROR_MAPPINGS: ErrorMapping[] = [
  // Login errors
  { pattern: 'invalid login credentials', message: 'Email ou mot de passe incorrect.' },
  { pattern: 'invalid email or password', message: 'Email ou mot de passe incorrect.' },
  { pattern: 'email not confirmed', message: "Cet email n'est pas confirmé. Vérifiez votre boîte mail." },
  { pattern: 'user not found', message: 'Email ou mot de passe incorrect.' },

  // Rate limiting
  { pattern: 'too many', message: 'Trop de tentatives. Réessayez dans quelques minutes.' },
  { pattern: 'rate limit', message: 'Trop de demandes. Réessayez plus tard.' },

  // Session errors
  { pattern: 'session_not_found', message: 'Votre session a expiré. Veuillez vous reconnecter.' },
  { pattern: 'invalid jwt', message: 'Votre session a expiré. Veuillez vous reconnecter.' },
  { pattern: 'jwt expired', message: 'Votre session a expiré. Veuillez vous reconnecter.' },
  { pattern: 'refresh_token', message: 'Votre session a expiré. Veuillez vous reconnecter.' },

  // Password errors
  { pattern: 'weak password', message: 'Le mot de passe est trop faible. Ajoutez des caractères spéciaux ou des chiffres.' },
  { pattern: 'password must be', message: 'Le mot de passe doit respecter les critères de sécurité.' },
  { pattern: 'same password', message: 'Le nouveau mot de passe doit être différent de l\'ancien.' },

  // Email errors
  { pattern: 'invalid email', message: 'L\'adresse email n\'est pas valide.' },
  { pattern: 'email already', message: 'Un compte existe déjà avec cet email.' },

  // Database/constraint errors
  { pattern: 'duplicate key', message: 'Cet utilisateur existe déjà.' },
  { pattern: 'violates unique constraint', message: 'Cet utilisateur existe déjà.' },

  // Network errors
  { pattern: 'network', message: 'Erreur de connexion. Vérifiez votre connexion internet.' },
  { pattern: 'fetch failed', message: 'Erreur de connexion. Vérifiez votre connexion internet.' },
  { pattern: 'failed to fetch', message: 'Erreur de connexion. Vérifiez votre connexion internet.' },
]

/**
 * Traduit une erreur Supabase Auth en message français user-friendly
 * @param error - L'erreur capturée (peut être un Error, un objet avec message, ou une string)
 * @param fallback - Message par défaut si aucun mapping trouvé
 * @returns Le message d'erreur traduit
 */
export function mapAuthError(
  error: unknown,
  fallback = 'Une erreur est survenue. Veuillez réessayer.'
): string {
  const message = extractErrorMessage(error).toLowerCase()

  for (const { pattern, message: mappedMessage } of AUTH_ERROR_MAPPINGS) {
    if (message.includes(pattern)) {
      return mappedMessage
    }
  }

  return fallback
}

/**
 * Extrait le message d'erreur d'un objet erreur de type inconnu
 */
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }
  return ''
}
